import { Button } from '@swegon-core/uic';
import {
    ButtonContainer,
    Container,
    Content,
    Description,
    DetailedErrorDescription,
    Title
} from './MissingDataPage.styles';
import { Header } from './Header';
import { useContext } from 'react';
import { Context as TranslationContext } from '../shared/contexts/TranslationContext';

export interface MissingDataPageProps {
    errorMessages?: string[];
}

type TranslationKeys =
    | 'ahuDesignLite.errorPage.title'
    | 'ahuDesignLite.errorPage.description.generic'
    | 'ahuDesignLite.errorPage.description.contactSupport'
    | 'ahuDesignLite.errorPage.description.contactSupport.url'
    | 'ahuDesignLite.errorPage.errorDetailsTitle'
    | 'ahuDesignLite.errorPage.refreshPage';

const FallbackTexts = (key: TranslationKeys): string => {
    const texts: { [key in TranslationKeys]: string } = {
        'ahuDesignLite.errorPage.title': 'Oops!',
        'ahuDesignLite.errorPage.description.generic':
            'Sorry, we had issues fetching data. Please try again in a couple of minutes.',
        'ahuDesignLite.errorPage.description.contactSupport':
            'If the issue persists, please contact support at',
        'ahuDesignLite.errorPage.description.contactSupport.url':
            'https://www.swegon.com/support/software/ahu-design/report-a-bug/',
        'ahuDesignLite.errorPage.refreshPage': 'Refresh page',
        'ahuDesignLite.errorPage.errorDetailsTitle': 'Error details'
    };
    return texts[key] ?? 'Unmapped error message';
};

const MissingDataPage = ({ errorMessages }: MissingDataPageProps) => {
    const {
        l,
        loading: translationsLoading,
        error: translationError
    } = useContext(TranslationContext);

    const translations = (key: TranslationKeys) => {
        if (!translationsLoading && translationError) {
            return FallbackTexts(key);
        } else {
            return l(key);
        }
    };

    return (
        <>
            <Header />
            <Container>
                <Content>
                    <Title>
                        {translations('ahuDesignLite.errorPage.title')}
                    </Title>
                    <Description>
                        <p>
                            {translations(
                                'ahuDesignLite.errorPage.description.generic'
                            )}
                        </p>
                        <p>
                            {translations(
                                'ahuDesignLite.errorPage.description.contactSupport'
                            )}
                            &nbsp;
                            <a
                                href={translations(
                                    'ahuDesignLite.errorPage.description.contactSupport.url'
                                )}
                                referrerPolicy="no-referrer"
                                target="_blank"
                            >
                                AHUD Support
                            </a>
                        </p>
                    </Description>
                    {errorMessages && (
                        <DetailedErrorDescription>
                            <summary>
                                {translations(
                                    'ahuDesignLite.errorPage.errorDetailsTitle'
                                )}
                            </summary>
                            {errorMessages.map((msg, index) => (
                                <p key={index}>{msg}</p>
                            ))}
                        </DetailedErrorDescription>
                    )}
                    <ButtonContainer>
                        <Button
                            label={translations(
                                'ahuDesignLite.errorPage.refreshPage'
                            )}
                            onClick={() => location.reload()}
                            buttonType="outlined"
                        />
                    </ButtonContainer>
                </Content>
            </Container>
        </>
    );
};

export default MissingDataPage;
